import { AfterViewInit, DestroyRef, Directive, ElementRef, inject, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { Scrollable, SCROLLABLE_ELEMENT } from './scrollable.directive';


const fromIntersectionObserver = (element: HTMLElement, root?: HTMLElement) => new Observable(subscriber => {
    const callback = (entries: IntersectionObserverEntry[]) => entries.forEach(entry => {
        subscriber.next(entry.isIntersecting);
    });
    const observer = new IntersectionObserver(callback, { threshold: 0, root });
    observer.observe(element);
    return () => observer.disconnect();
});


@Directive({
    selector: '[stickyTarget]'
})
export class StickyTargetDirective {

    private elementRef = inject(ElementRef);

    readonly intersecting = fromIntersectionObserver(this.elementRef.nativeElement);

    getBoundingClientRect() {
        return this.elementRef.nativeElement.getBoundingClientRect();
    }
}


@Directive({
    selector: '[stickyElement]'
})
export class StickyElementDirective implements AfterViewInit {

    private elementRef = inject(ElementRef);

    private renderer = inject(Renderer2);

    private stickyTarget = inject(StickyTargetDirective);

    private scrollableContainer = inject<Scrollable>(SCROLLABLE_ELEMENT);

    private destroyRef = inject(DestroyRef);

    ngAfterViewInit() {
        // turn off animation to prevent flickering
        this.renderer.setStyle(this.elementRef.nativeElement, 'animation', 'none');

        this.stickyTarget.intersecting.pipe(
            switchMap(intersecting => intersecting ? this.scrollableContainer.scrollingEvents : EMPTY),
            takeUntilDestroyed(this.destroyRef)
        )
            .subscribe(() => {
                const el = this.elementRef.nativeElement;
                const rootRect = this.scrollableContainer.getBoundingClientRect();
                const targetRect = this.stickyTarget.getBoundingClientRect();
                if (targetRect.top < rootRect.top) {
                    this.renderer.addClass(el, 'sticky');
                } else {
                    this.renderer.removeClass(el, 'sticky');
                }

                this.renderer.setStyle(el, 'transform', `translateY(${Math.max(rootRect.top - targetRect.top - 1, 0)}px)`);
            });
    }
}
